// Story 10.3 — Reducer puro del replay player. Aislado de React para tests.

export interface ReplayState {
  /** Cuántos mensajes están visibles (1..total). 0 = aún no arrancó / restart. */
  currentIndex: number;
  total: number;
  playing: boolean;
  speedMs: number;
}

export type ReplayAction =
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'step' }
  | { type: 'restart' }
  | { type: 'set-speed'; speedMs: number }
  | { type: 'set-total'; total: number }
  | { type: 'tick' };

export const REPLAY_DEFAULTS = {
  speedMs: 800,
  minSpeedMs: 200,
  maxSpeedMs: 3000,
} as const;

export function clampSpeed(value: number): number {
  if (!Number.isFinite(value)) return REPLAY_DEFAULTS.speedMs;
  return Math.min(Math.max(value, REPLAY_DEFAULTS.minSpeedMs), REPLAY_DEFAULTS.maxSpeedMs);
}

export function initReplayState(total: number, speedMs?: number): ReplayState {
  return {
    currentIndex: 0,
    total: Math.max(0, total | 0),
    playing: false,
    speedMs: clampSpeed(speedMs ?? REPLAY_DEFAULTS.speedMs),
  };
}

export function replayReducer(state: ReplayState, action: ReplayAction): ReplayState {
  switch (action.type) {
    case 'play': {
      if (state.total === 0) return state;
      // Si ya está al final, restart implícito al play.
      if (state.currentIndex >= state.total) {
        return { ...state, currentIndex: 0, playing: true };
      }
      return { ...state, playing: true };
    }
    case 'pause':
      return { ...state, playing: false };
    case 'step': {
      if (state.currentIndex >= state.total) return state;
      const next = state.currentIndex + 1;
      return { ...state, currentIndex: next, playing: state.playing && next < state.total };
    }
    case 'restart':
      return { ...state, currentIndex: 0, playing: false };
    case 'set-speed':
      return { ...state, speedMs: clampSpeed(action.speedMs) };
    case 'set-total': {
      const total = Math.max(0, action.total | 0);
      const currentIndex = Math.min(state.currentIndex, total);
      // P-267: si el total cae a 0 o `currentIndex` quedó al final tras el
      // clamp, salimos de play. Sin esto, el setTimeout loop re-arma con
      // `playing: true` aunque ya no haya nada que avanzar y dispara ticks
      // no-op cada frame mientras se arrastra el slider de velocidad.
      const playing = state.playing && total > 0 && currentIndex < total;
      return { ...state, total, currentIndex, playing };
    }
    case 'tick': {
      // tick = avance automático cuando playing. Si llegamos al final, pausa.
      if (!state.playing) return state;
      if (state.currentIndex >= state.total) return { ...state, playing: false };
      const next = state.currentIndex + 1;
      return {
        ...state,
        currentIndex: next,
        playing: next < state.total,
      };
    }
    default:
      return state;
  }
}

/** Helper para obtener `messages[0..currentIndex]` sin mutar. */
export function takeVisible<T>(messages: T[], state: ReplayState): T[] {
  return messages.slice(0, state.currentIndex);
}
